// react
import { ReactNode, createContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import axios, { setNittiService } from 'api/axios';

// third-party
import _ from 'lodash';

// types
import { RentalPlan, RentalOption } from 'types/rental';
import { CloudPlan, CloudOption, CloudDataPlan } from 'types/cloud';
import { CLOUD_BASE_PATH } from 'utils/constants';

// style

type DeliverySetting = { label: string; delivery: number; fee: number };

export type ConfigContextType = {
  isLocal: boolean;
  isDev: boolean;
  isProd: boolean;
  websiteId: number;
  serviceCode: string;
  basePath: string;
  shopHolidays: string[];
  deliverySetting: DeliverySetting[];
  rentalPlans: RentalPlan[];
  rentalOptions: RentalOption[];
  cloudPlans: CloudPlan[];
  cloudOptions: CloudOption[];
  cloudDataPlans: CloudDataPlan[];
};

type ConfigProviderProps = {
  children: ReactNode;
};

// サイト識別子
const RENTAL_CODE = process.env.REACT_APP_SERVICE_CODE as string;
const RENTAL_WEBSITE_ID = Number(process.env.REACT_APP_WEBSITE_ID);
const CLOUD_CODE = process.env.REACT_APP_CLOUD_SERVICE_CODE as string;
const CLOUD_WEBSITE_ID = Number(process.env.REACT_APP_CLOUD_WEBSITE_ID);

const getShopHolidays = async (): Promise<string[]> => {
  const { data: response } = await axios.get('/endpoint/shops/holidays/1');
  const { holidays } = response.data;
  return holidays;
};

const getDeliverySetting = async (): Promise<DeliverySetting[]> => {
  const { data: response } = await axios.get('/endpoint/shops/delivery-setting');
  const { deliverySetting } = response.data;
  return deliverySetting;
};

const getRentalPlans = async (): Promise<RentalPlan[]> => {
  const { data: response } = await axios.get('/endpoint/rental/get-plans');
  const { plans } = response.data;
  return _.map(plans, (plan) => {
    return {
      id: plan.id,
      model_name: plan.model_name,
      model_description: plan.model_description,
      name: plan.name,
      image_path: plan.image_path,
      description: plan.description,
      gb: plan.gb,
      prices: JSON.parse(plan.rental_plan_prices[0].prices)
    };
  });
};

const getRentalOptions = async (): Promise<RentalOption[]> => {
  const { data: response } = await axios.get('/endpoint/rental/get-options');
  const { options } = response.data;

  return _.map(options, (option) => {
    return {
      id: option.id,
      name: option.name,
      description: option.description,
      prices: JSON.parse(option.rental_option_prices[0].prices)
    };
  });
};

const getCloudPlans = async (): Promise<CloudPlan[]> => {
  const { data: response } = await axios.get('/endpoint/cloud/get-plans');
  const plans = Array.isArray(response?.data?.plans) ? response.data.plans : [];
  if (plans.length === 0) return [];

  return _.map(plans, (plan) => {
    return {
      id: plan.id,
      model_name: plan.model_name,
      model_description: plan.model_description,
      name: plan.name,
      image_path: plan.image_path,
      description: plan.description,
      gb: plan.gb,
      prices: JSON.parse(plan.cloud_plan_prices[0].prices)
    };
  });
};

const getCloudOptions = async (): Promise<CloudOption[]> => {
  const { data: response } = await axios.get('/endpoint/cloud/get-options');
  const options = Array.isArray(response?.data?.options) ? response.data.options : [];
  if (options.length === 0) return [];

  return _.map(options, (option) => {
    return {
      id: option.id,
      name: option.name,
      code: option.code,
      description: option.description,
      prices: JSON.parse(option.cloud_option_prices[0].prices)
    };
  });
};

const getCloudDataPlans = async (): Promise<CloudDataPlan[]> => {
  const { data: response } = await axios.get('/endpoint/cloud/get-data-plans');
  const data_plans = Array.isArray(response?.data?.data_plans) ? response.data.data_plans : [];
  if (data_plans.length === 0) return [];

  return _.map(data_plans, (plan) => {
    return {
      id: plan.id,
      name: plan.name,
      image_path: plan.image_path,
      service: plan.service,
      term: plan.term,
      gb: plan.gb,
      area: plan.area,
      type: plan.type,
      description: plan.description,
      price: plan.price,
      tax: plan.tax
    };
  });
};

const initialValue = {
  isLocal: process.env.REACT_APP_ENV_NAME === 'Local',
  isDev: process.env.REACT_APP_ENV_NAME === 'Development',
  isProd: process.env.REACT_APP_ENV_NAME === 'Production',
  websiteId: RENTAL_WEBSITE_ID,
  serviceCode: RENTAL_CODE,
  basePath: `/rental`,
  shopHolidays: [],
  deliverySetting: [],
  rentalPlans: [],
  rentalOptions: [],
  cloudPlans: [],
  cloudOptions: [],
  cloudDataPlans: []
};

const ConfigContext = createContext<ConfigContextType>(initialValue);

export const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const [configContext, setConfigContext] = useState<ConfigContextType>(initialValue);
  const location = useLocation();
  const service = useMemo(() => {
    if (location.pathname.startsWith(CLOUD_BASE_PATH)) return CLOUD_CODE;
    return RENTAL_CODE;
  }, [location.pathname]);

  useEffect(() => {
    setNittiService(service);
    (async () => {
      const shopHolidays = await getShopHolidays();
      const deliverySetting = await getDeliverySetting();
      const serviceContext: { [key: string]: any } = {
        shopHolidays,
        deliverySetting
      };

      if (service === process.env.REACT_APP_CLOUD_SERVICE_CODE) {
        const cloudPlans = await getCloudPlans();
        const cloudOptions = await getCloudOptions();
        const cloudDataPlans = await getCloudDataPlans();
        serviceContext.websiteId = CLOUD_WEBSITE_ID;
        serviceContext.serviceCode = CLOUD_CODE;
        serviceContext.basePath = CLOUD_BASE_PATH;
        serviceContext.cloudPlans = cloudPlans;
        serviceContext.cloudOptions = cloudOptions;
        serviceContext.cloudDataPlans = cloudDataPlans;
      } else {
        const rentalPlans = await getRentalPlans();
        const rentalOptions = await getRentalOptions();
        serviceContext.websiteId = RENTAL_WEBSITE_ID;
        serviceContext.serviceCode = RENTAL_CODE;
        serviceContext.basePath = `/rental`;
        serviceContext.rentalPlans = rentalPlans;
        serviceContext.rentalOptions = rentalOptions;
      }

      setConfigContext((prev) => ({ ...prev, ...serviceContext }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  return <ConfigContext.Provider value={configContext}>{children}</ConfigContext.Provider>;
};

export default ConfigContext;
